import React from "react";
import FormControl from "@mui/material/FormControl";
import styled from "styled-components";
import TextField from "@mui/material/TextField";
import { provinces } from "./siteutilities";
import { NavItems, Orgs } from "./navutilities";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { trackEvent } from "website/ga4utilities";
import { useProfileState } from "hooks/useProfileState";
import { useUserState } from "hooks/useUserState";

//  Checkboxes and Radio Buttons
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import ListSubheader from "@mui/material/ListSubheader";
import FormHelperText from "@mui/material/FormHelperText";

import { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { supabase } from "supabaseClient";
import AppContext from "AppContext";
import { createAPIClient } from "api/client";

// Profile states configuration
const PROFILE_STATES = {
  LOADING: {
    label: {
      text: 'Loading...',
      color: props => props.selectedNavItem?.primarycolor
    },
    header: {
      emoji: '🔍',
      title: 'Checking for profile...'
    },
    paragraph: 'Loading your profile information...'
  },
  BANNED: {
    label: {
      text: 'Profile Banned',
      color: '#FF0000',
      noPulse: true
    },
    header: {
      emoji: '🚫',
      title: 'Profile Banned'
    },
    paragraph: 'Your profile has been banned. Please contact support for more information.'
  },
  REJECTED: {
    label: {
      text: 'Profile Rejected',
      color: 'red',
      noPulse: true
    },
    header: {
      emoji: '❌',
      title: 'Profile Rejected'
    },
    paragraph: 'Your profile has been rejected. Please contact support for more information.'
  },
  WAITLISTED: {
    label: {
      text: 'On Waitlist',
      color: '#4CAF50',
      noPulse: true
    },
    header: {
      emoji: '⏳',
      title: 'Welcome!'
    },
    paragraph: 'Your profile is on the waitlist. We will notify you when a spot becomes available.'
  },
  ACCEPTED: {
    label: {
      text: 'Profile Accepted',
      color: '#4CAF50',
      noPulse: true
    },
    header: {
      emoji: '✅',
      title: 'Welcome!'
    },
    paragraph: 'Your profile has been accepted. Please complete any remaining steps to activate your account.'
  },
  ACTIVE: {
    label: {
      text: 'Profile Active',
      color: '#4CAF50',
      noPulse: true
    },
    header: {
      emoji: '👋',
      title: 'Welcome!'
    },
    paragraph: 'Your profile is active, visit your dashboard for the latest.'
  },
  UNDER_REVIEW: {
    label: {
      text: 'Under Review',
      color: 'rgb(255, 79, 0)'
    },
    header: {
      emoji: '🏗️',
      title: 'Profile found!'
    },
    paragraph: (
      <>
        Our Intake team will review your profile & you will receive an email
        once complete.{' '}
        <Link to="/news/release-notes-0-1">
          <u>Learn more here.</u>
        </Link>
      </>
    )
  },
  NEW_USER: {
    label: {
      text: "You're Logged In!",
      color: props => props.selectedNavItem?.primarycolor
    },
    header: {
      emoji: '👩🏾‍💻',
      title: 'Setup a profile.'
    },
    paragraph: 'Please ensure your information is completed in full and accurate for human review.'
  },
  DECLINED: {
    label: {
      text: 'Invite Declined',
      color: '#FF0000',
      noPulse: true
    },
    header: {
      emoji: '✕',
      title: 'Invite Declined'
    },
    paragraph: 'You have declined this organization invite. You can always apply again in the future.'
  }
};

const getProfileState = ({ loading, permissions, platformUser }) => {
  if (loading) return PROFILE_STATES.LOADING;
  if (!permissions) return PROFILE_STATES.LOADING;

  const {
    isRegistered,
    isUnderReview,
    isAccepted,
    isActive,
    isWaitlisted,
    isRejected,
    isBanned,
    isDeclined
  } = permissions;

  if (isBanned) return PROFILE_STATES.BANNED;
  if (isRejected) return PROFILE_STATES.REJECTED;
  if (isDeclined) return PROFILE_STATES.DECLINED;
  if (isWaitlisted) return PROFILE_STATES.WAITLISTED;
  if (isActive) return PROFILE_STATES.ACTIVE;
  if (isAccepted) return PROFILE_STATES.ACCEPTED;
  if (isUnderReview) return PROFILE_STATES.UNDER_REVIEW;
  if (platformUser && isRegistered) return PROFILE_STATES.NEW_USER;

  return PROFILE_STATES.NEW_USER;
};

export const ProfileForm = () => {
  const {
    platformUser,
    selectedNavItem,
    UserOrganization,
    setUserOrganization,
    setUserRole,
    userRole,
  } = useContext(AppContext);

  const { 
    permissions, 
    userData, 
    isLoading: userLoading, 
    refreshUserData, 
    setUserState, 
    organizations,
    data: rawData  // Add this to get access to raw data
  } = useUserState();

  const [profileState, setProfileState] = useState(() =>
    getProfileState({ loading: userLoading, permissions, platformUser })
  );

  const userProfile = useMemo(() => userData || null, [userData]);

  const [submit, setSubmit] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(permissions?.isUnderReview ? "Update Profile?" : "Submit for Review");
  const [saveMessage, setSaveMessage] = useState(permissions?.hasDraft ? "Update Draft ?" : "Save Draft ?");

  const navigate = useNavigate();

  // Initialize form data from user profile
  const [formData, setFormData] = useState({
    userLocation: userProfile?.profiledata?.userLocation || "",
    userRoles: userProfile?.profiledata?.userRoles || "",
    orgName: userProfile?.profiledata?.orgName || "",
    orgSize: userProfile?.profiledata?.orgSize || "",
    orgDescription: userProfile?.profiledata?.orgDescription || "",
    userFname: userProfile?.profiledata?.userFname || "",
    userLname: userProfile?.profiledata?.userLname || "",
    gender: userProfile?.profiledata?.gender || "",
    projectDescription: userProfile?.profiledata?.projectDescription || "",
    challengeDescription: userProfile?.profiledata?.challengeDescription || "",
    profileUrl: userProfile?.profiledata?.profileUrl || "",
    supportOptions: userProfile?.profiledata?.supportOptions || {
      network: false,
      mentoring: false,
      funding: false,
      programs: false,
      resources: false,
    },
  });

  // Update profile state when permissions change
  useEffect(() => {
    setProfileState(getProfileState({ loading: userLoading, permissions, platformUser }));
  }, [permissions, userLoading, platformUser]);

  // Update form data when profile changes
  useEffect(() => {
    if (userProfile?.profiledata) {
      setFormData(prevData => ({
        ...prevData,
        ...userProfile.profiledata
      }));
      setUserRole(userProfile.profiledata.userRoles);
      setSubmitMessage(permissions?.isUnderReview ? "Update Profile?" : "Submit for Review");
    }
  }, [userProfile, setUserRole, permissions]);

  // Update organization when user data changes
  useEffect(() => {
    if (userData?.custom?.organization) {
      const matchingOrgItem = Orgs.find(
        (item) => item.route.toLowerCase() === userData.custom.organization.toLowerCase()
      );
      if (matchingOrgItem) {
        setUserOrganization(matchingOrgItem);
      }
    }
  }, [userData, setUserOrganization]);

  // Update save message when profile changes
  useEffect(() => {
    setSaveMessage(permissions?.hasDraft ? "Update Draft ?" : "Save Draft ?");
  }, [permissions]);

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleSave = async () => {
    setSaveMessage(permissions?.hasDraft ? "Updating..." : "Saving...");
    await delay(1000);
    try {
      const apiClient = await createAPIClient();
      const response = await apiClient.profiles.updateProfile({
        profileData: formData,
        submitted: false
      });

      await delay(1000);
      setSaveMessage(permissions?.hasDraft ? "Updated ✅" : "Saved ✅");
      await delay(1000);
      trackEvent("user_profile_updated");

      // Update form data and context state with the response
      if (response?.raw?.profile?.profiledata) {
        // Update form data
        setFormData(prevData => ({
          ...prevData,
          ...response.raw.profile.profiledata
        }));

        // Transform API response to match UserContext state structure
        const transformedState = {
          isLoading: false,
          error: null,
          data: {
            profile: response.raw.profile,
            custom: response.raw.custom
          },
          userData: {
            ...userData,
            profiledata: response.raw.profile.profiledata
          },
          teams: response.teams || [],
          organizations: response.organizations || [],
          permissions: {
            ...permissions,
            ...response.permissions,
            hasDraft: true
          }
        };

        // Update UserContext state with transformed data
        setUserState(transformedState);
      }

      await delay(1000);
      setSaveMessage(response.permissions?.hasDraft ? "Update Draft ?" : "Save Draft ?");
    } catch (error) {
      console.error("Error saving profile:", error.message);
      setSaveMessage("Error saving ❌");
      await delay(1000);
      setSaveMessage(permissions?.hasDraft ? "Update Draft ?" : "Save Draft ?");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmit(true);
    setSubmitMessage(permissions?.isUnderReview ? "Updating..." : "Submitting...");

    try {
      const apiClient = await createAPIClient();
      const response = await apiClient.profiles.updateProfile({
        profileData: formData,
        submitted: true
      });

      await delay(1000);
      setSubmitMessage(permissions?.isUnderReview ? "Updated ✅" : "Submitted ✅");
      await delay(1000);
      trackEvent("user_profile_submitted");

      // Update form data and context state with the response
      if (response?.raw?.profile?.profiledata) {
        // Update form data
        setFormData(prevData => ({
          ...prevData,
          ...response.raw.profile.profiledata
        }));

        // Transform API response to match UserContext state structure
        const transformedState = {
          isLoading: false,
          error: null,
          data: {
            profile: response.raw.profile,
            custom: response.raw.custom
          },
          userData: {
            ...userData,
            profiledata: response.raw.profile.profiledata
          },
          teams: response.teams || [],
          organizations: response.organizations || [],
          permissions: {
            ...permissions,
            ...response.permissions,
            isUnderReview: true,
            hasDraft: false
          }
        };

        // Update UserContext state with transformed data
        setUserState(transformedState);
      }

      // Update submit message based on new permissions
      setSubmitMessage(response.permissions?.isUnderReview ? "Update Profile?" : "Submit for Review");
      setSubmit(false);
    } catch (error) {
      console.error("Error submitting profile:", error.message);
      setSubmitMessage("Error ❌");
      setSubmitMessage(permissions?.isUnderReview ? "Update Profile?" : "Submit for Review");
      setSubmit(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (name === "userRoles" && !UserOrganization) {
      setUserRole(value);
    }
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      supportOptions: {
        ...prevData.supportOptions,
        [name]: checked,
      },
    }));
  };

  return (
    <>
      <ProfileSignUp className="ProfileSignup">
        {(() => {
          return (
            <>
              <Label
                $color={typeof profileState.label.color === 'function' ? profileState.label.color({ selectedNavItem }) : profileState.label.color}
                $noPulse={profileState.label.noPulse}
              >
                {profileState.label.text}
              </Label>

              <ProfileHeader className="ProfileHeader">
                <Emoji>{profileState.header.emoji}</Emoji>
                <Title>{profileState.header.title}</Title>
              </ProfileHeader>

              <Paragraph>
                {profileState.paragraph}
              </Paragraph>
            </>
          );
        })()}

        <form disabled={userLoading}>
          <SectionHeading className="ProfileHeading">My Profile</SectionHeading>
          <FormHelperText>
            For community safety, your identity will be verified. 🪪
          </FormHelperText>
          <InputContainer className="RoleInput">
            <FormControl fullWidth>
              <InputLabel shrink id="role-select-label">
                Role
              </InputLabel>
              <Select
                name="userRoles"
                labelId="role-select-label"
                id="role-select"
                label="Role"
                value={userLoading ? "" : formData.userRoles}
                onChange={handleInputChange}
                displayEmpty
                size="small"
                disabled={userLoading || submit}
                renderValue={(selected) => {

                  if (userLoading) {
                    return "...";
                  }

                  if (!selected) {
                    return "🔎 Select your Role";
                  }

                  const roleData = NavItems.find(
                    (navItem) => navItem.role === selected
                  );

                  if (roleData) {
                    const { emoji, role } = roleData;
                    return (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {emoji} &nbsp;
                        {role}
                      </div>
                    );
                  }
                  return selected;
                }}
              >
                {NavItems.map(({ role, emoji }) => (
                  <MenuItem key={role} value={role}>
                    {emoji} {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </InputContainer>
          <InputContainer className="LocationInput">
            <FormControl fullWidth>
              <InputLabel
                shrink
                // className="locationLabel"
                id="location-select-label"
              >
                Location
              </InputLabel>
              <Select
                name="userLocation"
                labelId="location-select-label"
                id="location-select"
                label="Location"
                value={userLoading ? "..." : formData.userLocation}
                onChange={handleInputChange}
                displayEmpty
                size="small"
                disabled={userLoading || submit}
                renderValue={(selected) => {
                  if (!selected) {
                    return "🔎 Select your Location";
                  }
                  const provinceData = Object.entries(provinces).find(
                    ([_, { cities }]) =>
                      cities.some((city) => city.name === selected)
                  );
                  if (provinceData) {
                    const [_, { name, image }] = provinceData;
                    return (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {image} &nbsp;
                        {selected}
                      </div>
                    );
                  }
                  return selected;
                }}
              >
                {Object.entries(provinces).map(
                  ([value, { name, image, cities }]) => [
                    <ListSubheader key={value}>
                      {image} {name}
                    </ListSubheader>,
                    ...cities.map((city) => (
                      <MenuItem key={city.name} value={city.name}>
                        {city.name}
                      </MenuItem>
                    )),
                  ]
                )}
              </Select>
            </FormControl>
          </InputContainer>

          <InputContainer>
            <Input
              type="text"
              label="First Name"
              variant="outlined"
              size="small"
              name="userFname"
              value={userLoading ? "..." : formData.userFname}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              disabled={userLoading || submit}
            />
          </InputContainer>
          <InputContainer>
            <Input
              type="text"
              label="Last Name"
              variant="outlined"
              size="small"
              name="userLname"
              value={userLoading ? "..." : formData.userLname}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              disabled={userLoading || submit}
            />
          </InputContainer>

          <InputContainer>
            <FormControl fullWidth>
              <InputLabel shrink id="gender-select-label">
                Gender
              </InputLabel>
              <Select
                name="gender"
                labelId="gender-select-label"
                id="gender-select"
                label="Gender"
                value={userLoading ? "" : formData.gender}
                onChange={handleInputChange}
                displayEmpty
                size="small"
                disabled={userLoading || submit}
                renderValue={(selected) => {
                  if (userLoading) return "...";
                  if (!selected) return "🔎 Select your Gender";
                  return selected;
                }}
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Non-binary">Non-binary</MenuItem>
                <MenuItem value="Prefer-not-to-say">Prefer not to say</MenuItem>
              </Select>
            </FormControl>
            <FormHelperText>
              Defines eligibility for Women Founder programs.
            </FormHelperText>
          </InputContainer>
          <OrganizationHeading>Organization Details</OrganizationHeading>
          <InputContainer>
            <Input
              type="text"
              label="Organization Name"
              variant="outlined"
              size="small"
              name="orgName"
              value={userLoading ? "..." : formData.orgName}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              disabled={userLoading || submit}
            />
          </InputContainer>
          <InputContainer>
            <FormControl fullWidth>
              <InputLabel shrink className="orgSizeLabel" id="orgSize-label">
                Organization Size
              </InputLabel>
              <Select
                labelId="orgSize-label"
                label="Organization Size"
                id="org-size"
                value={userLoading ? "" : formData.orgSize}
                name="orgSize"
                onChange={handleInputChange}
                displayEmpty
                size="small"
                disabled={userLoading || submit}
                renderValue={(selected) => {
                  if (userLoading) return "...";
                  if (!selected) return "🔎 Select Organization Size";
                  return selected;
                }}
              >
                <MenuItem value="1">1</MenuItem>
                <MenuItem value="3 - 10">3 - 10</MenuItem>
                <MenuItem value="12 - 25">11 - 25</MenuItem>
                <MenuItem value="26 - 50">26 - 50</MenuItem>
                <MenuItem value="51 - 100">51 - 100</MenuItem>
                <MenuItem value="100+">100 +</MenuItem>
              </Select>
            </FormControl>
          </InputContainer>
          <InputContainer>
            <Input
              className="multiline"
              type="text"
              label="Just a few words about your company"
              variant="outlined"
              size="small"
              multiline
              name="orgDescription"
              value={userLoading ? "..." : formData.orgDescription}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              disabled={userLoading || submit}
            />
          </InputContainer>
          {/* <SectionHeading>Project Details</SectionHeading>
          <InputContainer>
            <Input
              className="multiline"
              type="text"
              label="Describe your current project"
              variant="outlined"
              size="small"
              multiline
              name="projectDescription"
              value={userLoading ? "..." : formData.projectDescription}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              disabled={userLoading || submit}
            />
          </InputContainer>

          <InputContainer>
            <Input
              className="multiline"
              type="text"
              label="What's your biggest challenge?"
              variant="outlined"
              size="small"
              multiline
              name="challengeDescription"
              value={formData.challengeDescription}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              disabled={userLoading || submit}
            />
          </InputContainer> */}
          <InputContainer>
            <Input
              type="text"
              label="Organization or Personal URL"
              variant="outlined"
              size="small"
              name="profileUrl"
              value={userLoading ? "..." : formData.profileUrl}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              disabled={userLoading || submit}
            />
          </InputContainer>

          <OrganizationHeading>Goals</OrganizationHeading>

          <InputContainer className="checklist">
            <SectionHeading className="SupportHeading">
              Can we support with any of the following?
            </SectionHeading>
            <div className="ChecklistGroup">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.supportOptions.network}
                    onChange={handleCheckboxChange}
                    name="network"
                    disabled={userLoading || submit}
                  />
                }
                label="Network"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.supportOptions.mentoring}
                    onChange={handleCheckboxChange}
                    name="mentoring"
                    disabled={userLoading || submit}
                  />
                }
                label="Mentoring"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.supportOptions.funding}
                    onChange={handleCheckboxChange}
                    name="funding"
                    disabled={userLoading || submit}
                  />
                }
                label="Funding"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.supportOptions.programs}
                    onChange={handleCheckboxChange}
                    name="programs"
                    disabled={userLoading || submit}
                  />
                }
                label="Programs"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.supportOptions.resources}
                    onChange={handleCheckboxChange}
                    name="resources"
                    disabled={userLoading || submit}
                  />
                }
                label="Resources"
              />
            </div>
          </InputContainer>
        </form>
      </ProfileSignUp>
      <ButtonContainer className="ButtonContainer">
        {!userLoading && permissions?.isAccepted && (
          <>
            <Button 
              className="DeclineButton" 
              onClick={async () => {
                try {
                  setSubmit(true);
                  const apiClient = await createAPIClient();
                  
                  // Find the organization with 'accepted' status
                  let acceptedOrg = null;
                  
                  // Debug log to see what organizations data we have
                  console.log("Organizations data for decline:", {
                    member: organizations?.member,
                    admin: organizations?.admin,
                    raw: rawData?.organizations
                  });
                  
                  // First try to find in transformed data
                  if (organizations?.member?.length) {
                    acceptedOrg = organizations.member.find(org => org.status === 'accepted');
                  }
                  
                  if (!acceptedOrg && organizations?.admin?.length) {
                    acceptedOrg = organizations.admin.find(org => org.status === 'accepted');
                  }
                  
                  // If not found in transformed data, try raw data
                  if (!acceptedOrg && rawData?.organizations) {
                    const rawAcceptedOrg = rawData.organizations.find(org => org.status === 'accepted');
                    if (rawAcceptedOrg) {
                      acceptedOrg = {
                        organizationId: rawAcceptedOrg.organization_id
                      };
                    }
                  }
                  
                  if (!acceptedOrg) {
                    console.error("No accepted organization found in:", {
                      transformed: organizations,
                      raw: rawData?.organizations
                    });
                    throw new Error('No organization with "accepted" status found in your profile');
                  }
                  
                  const response = await apiClient.profiles.updateStatus({
                    status: 'declined',
                    organization_id: acceptedOrg.organizationId
                  });

                  if (response) {
                    // Update the UserContext state with the new permissions
                    setUserState(response);
                    trackEvent("user_declined_invite");
                  }
                } catch (error) {
                  console.error("Error declining invite:", error);
                } finally {
                  setSubmit(false);
                }
              }}
              type="button" 
              disabled={userLoading || submit}
            >
              {submit ? "Declining..." : "Decline Invite"}
            </Button>
            <Button 
              className="AcceptButton" 
              onClick={async () => {
                try {
                  setSubmit(true);
                  const apiClient = await createAPIClient();
                  
                  // Find the organization with 'accepted' status
                  let acceptedOrg = null;
                  
                  // Debug log to see what organizations data we have
                  console.log("Organizations data for accept:", {
                    member: organizations?.member,
                    admin: organizations?.admin,
                    raw: rawData?.organizations
                  });
                  
                  // First try to find in transformed data
                  if (organizations?.member?.length) {
                    acceptedOrg = organizations.member.find(org => org.status === 'accepted');
                  }
                  
                  if (!acceptedOrg && organizations?.admin?.length) {
                    acceptedOrg = organizations.admin.find(org => org.status === 'accepted');
                  }
                  
                  // If not found in transformed data, try raw data
                  if (!acceptedOrg && rawData?.organizations) {
                    const rawAcceptedOrg = rawData.organizations.find(org => org.status === 'accepted');
                    if (rawAcceptedOrg) {
                      acceptedOrg = {
                        organizationId: rawAcceptedOrg.organization_id
                      };
                    }
                  }
                  
                  if (!acceptedOrg) {
                    console.error("No accepted organization found in:", {
                      transformed: organizations,
                      raw: rawData?.organizations
                    });
                    throw new Error('No organization with "accepted" status found in your profile');
                  }
                  
                  const response = await apiClient.profiles.updateStatus({
                    status: 'active',
                    organization_id: acceptedOrg.organizationId
                  });

                  if (response) {
                    // Update the UserContext state with the new permissions
                    setUserState(response);
                    trackEvent("user_accepted_invite");
                    // Redirect to dashboard after successful acceptance
                    navigate('/dashboard');
                  }
                } catch (error) {
                  console.error("Error accepting invite:", error);
                } finally {
                  setSubmit(false);
                }
              }} 
              type="button" 
              disabled={userLoading || submit}
            >
              {submit ? "Accepting..." : "Accept & Join Organization"}
            </Button>
          </>
        )}

        {!userLoading && !permissions?.isActive && !permissions?.isUnderReview && !permissions?.isAccepted && (
          <Button className="SaveButton" onClick={handleSave} type="submit" disabled={userLoading || submit}>
            {saveMessage}
          </Button>
        )}

        {!userLoading && !permissions?.isAccepted && (
          <Button className="SubmitButton" onClick={handleSubmit} type="submit" disabled={userLoading || submit}>
            {userLoading ? "Loading..." : permissions?.isActive ? "Update Profile?" : submitMessage}
          </Button>
        )}
      </ButtonContainer>
    </>
  );
};


const ProfileSignUp = styled(FormControl)`
  display: flex;
  flex-direction: column;
  align-items: left;
  box-sizing: border-box;
  box-shadow: 0px 0px 5px 1px #f1f1f1;
  text-align: left;
  width: 100%;
  background: #fff;
  box-shadow: 0px 0px 10px 2px rgba(212, 212, 212, 0.24);
  box-sizing: border-box;
  border-radius: 5px 5px 0px 0px;
  position: relative;
  height: 100%;
  overflow: hidden;
  @media screen and (max-width: 640px) {
    overflow-y: scroll;
  }
  h1 {
    font-size: 21px;
    margin: 8px 0px 25px;
  }
  h4 {
    padding: 30px 0px 3px;
    margin: 0px 40px 0px 30px;
  }
  h5 {
    margin: 0px;
    margin-top: 10px;
  }
  .border {
    height: 2px;
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;
    box-shadow: 0px 0px 10px 2px rgba(212, 212, 212, 0.24);
    background: #f9f9f9;
  }

  &:nth-child(even) {
  }
  form {
    // max-height: 400px;
    overflow-y: scroll;
    margin-top: 25px;
    padding-top: 10px;
    padding: 20px 30px 25px 30px;
    box-shadow: 0px 0px 5px 1px #f1f1f1;
    gap: 20px;
    display: flex;
    flex-direction: column;
    height: fit-content;
    opacity: ${(props) => (props.disabled ? 0 : .9)} !important;
    input {
    }
    @media screen and (max-width: 640px) {
      overflow-y: visible;
      padding: 20px;
      padding-bottom: 30px;
    }
  }
`;

const InputContainer = styled.div`
  input {
    text-transform: capitalize;
  }
  &.checklist {
    margin-top: -20px;
    color: black;
    opacity: 0.8;
    label {
      min-width: 120px;
    }
    span {
      font-size: 12px;
      font-family: pt sans;
    }
    h5 {
      margin-top: 8px;
      margin-bottom: 10px;
      opacity: 0.8;
      font-weight: 500;
      font-size: 14px;
    }
    .ChecklistGroup {
      margin: 12px 10px;
    }
    svg {
      color: black;
    }
  }
  .select {
    width: 100%;
    min-width: 300px;
  }
  .locationLabel,
  .roleLabel {
    color: rgba(0, 0, 0, 0.6);
    font-size: 12.5px;
    margin-left: 15px;
    margin-bottom: -7px;
    margin-top: -6px;
    display: flex;
    @media screen and (max-width: 640px) {
      // margin-top: 5px;
    }
  }
  .roleLabel {
    // margin-bottom: 10px;
  }
  &.LocationInput {
    margin-bottom: 2px;
    div {
      width: 90%;
    }
  }
  &.RoleInput {
    div {
      width: 90%;
    }
  }
`;

const SectionHeading = styled.h5``;

const Input = styled(TextField)`
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
  margin-bottom: 20px;
  font-family: Roboto;
  color: red !important;
  &.multiline {
    textarea {
      min-height: 100px;
    }
      margin-top: 10px;
  }
}
`;

const ProfileHeader = styled.div`
  display: flex;
  flex-direction: row;
  margin: 5px 0px 10px;
`;

const OrganizationHeading = styled.h5`
  padding-bottom: 10px;
`;

const Emoji = styled.span`
  display: flex;
  margin-right: 5px;
  margin-top: 7px;
  margin-left: 20px;
  margin-bottom: 0px;
`;

const Title = styled.h1`
  display: flex;
  font-size: 16.5px !important;
  color: #000;
  font-family: "PT Sans";
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 0.75px;
  margin-bottom: 0px !important;
  max-width: 95%;
  @media screen and (max-width: 768px) {
    max-width: 95%;
  }
`;

const Paragraph = styled.p`
  margin-bottom: 0px;
  margin-left: 27px;
  margin-top: 0px;
  max-width: 80%;
  line-height: 22px;
  font-size: 14.5px;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  background: white;
  position: relative;
  bottom: 0px;
  overflow: hidden;
  box-sizing: border-box;
  box-shadow: 0px 0px 5px 1px #f1f1f1;
  background: white;
  z-index: 2;
  border-radius: 0px 0px 5px 5px;
  width: 102%;
  margin-bottom: -5px;
  padding: 30px;
  gap: 10px;
  @media screen and (max-height: 768px) {
  }
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  background-color: #007bff;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  padding: 6px 16px 6px 16px;
  gap: 8px;
  height: 40px;
  text-transform: uppercase;
  font-family: Roboto;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.4;
  text-align: left;
  background: black;
  box-shadow: 0px 0px 5px 1px #f1f1f1;
  &:disabled {
    // Styles when the button is disabled
    opacity:.7;
    cursor: wait;
  }
  &:hover {
    background: #000;
  }
  &.SaveButton {
    background: white;
    color: black;
    border: 2px solid black;
    &:hover {
      background: #f1f1f1;

    }
      &.SubmitButton {
      min-width: 160px;
    }
  }
  &.AcceptButton {
    background: #4CAF50;
    min-width: 200px;
    &:hover {
      background: #388E3C;
    }
  }

  &.DeclineButton {
    background: white;
    color: #FF0000;
    border: 2px solid #FF0000;
    min-width: 120px;
    &:hover {
      background: #FFF5F5;
    }
  }
`;

const Label = styled.span`
  font-weight: bold;
  border-radius: 3px;
  background: ${(props) => props.$color};
  color: white;
  padding: 8px 15px 7px;
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 700;
  font-family: "PT Sans", sans-serif;
  letter-spacing: 1px;
  margin: 20px;
  width: fit-content;
  ${props => !props.$noPulse && props.$color !== "#4CAF50" && `
    animation: pulse 2.6s infinite ease-in-out;
    @keyframes pulse {
      0% {
        background-color: ${props.$color};
      }
      50% {
        background-color: ${props.theme.colors.black};
      }
      100% {
        background-color: ${props.$color};
      }
    }
  `}
`;
